/* eslint-disable no-nested-ternary */
import { useEffect, useState, useRef } from "react";
import { InputAdornment } from "@mui/material";
import { IoMdLock } from "react-icons/io";
import MaterialUI from "material-UI";
import { useParams } from "react-router-dom";
import { LuInfo } from "react-icons/lu";
import * as S from "./styles";

interface Data {
  ato: string;
  anualSolicitada: string;
  valorMaximoAnualPermitido: string;
  proSolutoPosChavesRadio: string;
  descontoSolicitadoRadio: string;
  porcentagem: string;
  numeroParcelas: string;
  descontoSolicitadoInput: string;
  valorDesconto: string;
  valorPropostaUnidade: string;
  avaliacaoConsiderada: string;
  fgtsSubsidio: string;
  financiamentoUtilizado: string;
  fgtsUtilizado: string;
  pmtUtilizada: string;
  proSolutoPreChaves: string;
  proSolutoPreChaves2: string;
  proSolutoPosChaves: string;
  proSolutoPosChaves2: string;
  comprometimentoMensaisMedio: string;
  comprometimentoMensaisMaximo: string;
}

interface Props {
  formResumoPagamento: any;
  setFormResumoPagamento: any;
  dataResumoPagamento: any;
  dataDadosUnidade: any;
  dataAplicarDesconto: any;
  mutateAplicarDesconto: any;
  isLoadingResumoPagamento: boolean;
  dataPosChaves: any;
  mutatePosChaves: any;
  handleValidateDadosAvaliacaoAto: any;
  errorAto: boolean;
  setErrorAto: any;
  setSaveValueReal: any;
  mutateResumoPagamento: any;
  isFieldFocused: boolean;
  setIsFieldFocused: any;
  dataGerarFluxo: any;
  isSucessGerarFluxo: any;
  dataEditarSerie: any;
  fluxoResetado: boolean;
}

export default function ResumoPagamento(props: Props) {
  const params: any = useParams();
  const [verifyReset, setVerifyReset] = useState(false);
  const [verifyInfo, setVerifyInfo] = useState(false);
  const [anualSolicitadaBackup, setAnualSolicitadaBackup] =
    useState<string>("");
  const prevIsAnualPersonalizadaDisabled = useRef<boolean>(false);

  // Verifica se anualPersonalizada é true (vem da resposta de editar-serie/vivaz/v1)
  const anualPersonalizada =
    props.dataEditarSerie?.data?.anualPersonalizada === true ||
    props.dataResumoPagamento?.data?.anualPersonalizada === true ||
    props.dataGerarFluxo?.data?.anualPersonalizada === true;

  // O campo deve estar desabilitado SEMPRE que anualPersonalizada é true
  // EXCETO quando o fluxo foi resetado (fluxoResetado = true), aí libera o campo
  const isAnualPersonalizadaDisabled =
    anualPersonalizada && !props.fluxoResetado;

  // Salva o valor antes de bloquear quando anualPersonalizada for true
  useEffect(() => {
    // Só executa quando muda de false para true
    if (
      isAnualPersonalizadaDisabled &&
      !prevIsAnualPersonalizadaDisabled.current
    ) {
      const currentValue = props.formResumoPagamento.anualSolicitada;
      if (currentValue) {
        // Salva o valor no backup
        setAnualSolicitadaBackup(currentValue);
        // IMPORTANTE: NÃO limpa o formResumoPagamento.anualSolicitada
        // O valor permanece no estado para ser usado na API quando resetar
        // Apenas o campo visual fica vazio (controlado pelo value do TextField)
      }
    }
    prevIsAnualPersonalizadaDisabled.current = isAnualPersonalizadaDisabled;
  }, [isAnualPersonalizadaDisabled]);

  // Quando o fluxo é resetado, o valor já está no formResumoPagamento.anualSolicitada
  // porque nunca foi limpo. Apenas limpa o backup.
  useEffect(() => {
    if (!props.isSucessGerarFluxo && anualSolicitadaBackup) {
      // O valor já está no formResumoPagamento.anualSolicitada, não precisa restaurar
      // Apenas limpa o backup
      setAnualSolicitadaBackup("");
    }
  }, [props.isSucessGerarFluxo, anualSolicitadaBackup]);

  const formatCurrency = (value: string) => {
    const cleanedValue = value.replace(/\D/g, "");
    if (cleanedValue === "00") {
      return "";
    }
    const formattedValue = (Number(cleanedValue) / 100).toFixed(2);
    return `${formattedValue
      .replace(".", ",")
      .replace(/\B(?=(\d{3})+(?!\d))/g, ".")}`;
  };

  const formatCurrencyLimitedAt5 = (value: string) => {
    const cleanedValue = value.replace(/\D/g, "");
    if (cleanedValue === "00") {
      return "";
    }
    let numericValue = Number(cleanedValue) / 100;
    if (
      numericValue >
      props.dataResumoPagamento?.data?.porcentagemProSolutoPosChaves
    ) {
      numericValue = 0;
    }
    const formattedValue = numericValue.toFixed(2);
    return `${formattedValue
      .replace(".", ",")
      .replace(/\B(?=(\d{3})+(?!\d))/g, ".")}`;
  };

  const formatCurrencyLimited = (value: string) => {
    const cleanedValue = value.replace(/\D/g, "");
    if (cleanedValue === "00") {
      return "";
    }
    let numericValue = Number(cleanedValue) / 100;
    if (numericValue > props.dataResumoPagamento?.data?.financiamentoLimite) {
      numericValue = 0;
    }
    const formattedValue = numericValue.toFixed(2);
    return `${formattedValue
      .replace(".", ",")
      .replace(/\B(?=(\d{3})+(?!\d))/g, ".")}`;
  };

  const toReal = (value: number) =>
    value?.toLocaleString("pt-br", { minimumFractionDigits: 2 }) || "";

  useEffect(() => {
    if (props.dataResumoPagamento) {
      const toReal = (value: number) =>
        value?.toLocaleString("pt-br", { minimumFractionDigits: 2 }) || "";

      const data = props.dataResumoPagamento?.data;

      props.setFormResumoPagamento((prevData: Data) => ({
        ...prevData,
        ato: data.valorAto ? toReal(data.valorAto) : prevData.ato,
        anualSolicitada: data?.valorAnualSolicitada
          ? toReal(data?.valorAnualSolicitada)
          : prevData.anualSolicitada,
        proSolutoPosChavesRadio: data?.proSolutoPosChavesSelecionado
          ? "1"
          : "2",
        descontoSolicitadoRadio: data?.descontoSolicitadoEnum
          ? data?.descontoSolicitadoEnum?.toString() || "0"
          : prevData.descontoSolicitadoRadio,
        porcentagem: data?.porcentagemProSolutoPosChaves
          ? toReal(data?.porcentagemProSolutoPosChaves)
          : prevData.porcentagem,
        numeroParcelas: data?.numeroParcelasProSolutoPosChaves?.toString(),
        descontoSolicitadoInput: data?.descontoSolicitadoEnum
          ? data?.descontoSolicitadoEnum === 1
            ? toReal(data?.descontoReais)
            : data?.descontoSolicitadoEnum === 2
            ? toReal(data?.descontoPorcentagem)
            : ""
          : prevData.descontoSolicitadoInput,
        valorDesconto: data?.descontoSolicitadoEnum
          ? data?.descontoSolicitadoEnum === 2
            ? toReal(data?.descontoReais)
            : data?.descontoSolicitadoEnum === 1
            ? toReal(data?.descontoPorcentagem)
            : ""
          : prevData.valorDesconto,
        valorPropostaUnidade: prevData.valorPropostaUnidade
          ? prevData.valorPropostaUnidade
          : toReal(data?.valorDaPropostaParaUnidade),
        // Valor calculado pelo backend, exibido apenas para consulta.
        avaliacaoConsiderada: toReal(data?.avaliacaoConsiderada),
        fgtsSubsidio: toReal(data?.fgtsSubsidio),
        financiamentoUtilizado: toReal(data?.financiamentoUtilizado),
        fgtsUtilizado: toReal(data?.fgtsUtilizado),
        pmtUtilizada: toReal(data?.pmtUtilizada),
        proSolutoPreChaves: toReal(data?.proSolutoPreChaves?.valor),
        proSolutoPreChaves2: toReal(data?.proSolutoPreChaves?.porcentagem),
        proSolutoPosChaves: data?.proSolutoPosChaves?.valor
          ? toReal(data?.proSolutoPosChaves?.valor)
          : prevData.proSolutoPosChaves,
        proSolutoPosChaves2: data?.proSolutoPosChaves?.porcentagem
          ? toReal(data?.proSolutoPosChaves?.porcentagem)
          : prevData.proSolutoPosChaves2,
        comprometimentoMensaisMedio: toReal(data?.comprometimentoMensaisMed),
        comprometimentoMensaisMaximo: toReal(data?.comprometimentoMensaisMax),
      }));
    }
  }, [props.dataResumoPagamento]);

  useEffect(() => {
    if (props.dataAplicarDesconto && verifyInfo) {
      props.setFormResumoPagamento((prevData: Data) => ({
        ...prevData,
        valorDesconto:
          prevData.descontoSolicitadoRadio === "1"
            ? props.dataAplicarDesconto?.data?.desconto?.valorPorcentagem
            : props.dataAplicarDesconto?.data?.desconto?.valorReais,
        valorPropostaUnidade:
          props.dataAplicarDesconto?.data?.valorPropostaParaUnidade,
      }));
      setVerifyInfo(false);
    } else if (verifyReset) {
      const toReal = (value: number) =>
        value?.toLocaleString("pt-br", { minimumFractionDigits: 2 }) || "";
      props.setFormResumoPagamento((prevData: Data) => ({
        ...prevData,
        valorDesconto: "",
        descontoSolicitadoInput: "",
        valorPropostaUnidade: toReal(props.dataDadosUnidade?.valorTabela),
      }));
      setVerifyReset(false);
    }
  }, [verifyReset, props.dataAplicarDesconto]);

  useEffect(() => {
    if (props.dataPosChaves) {
      const toReal = (value: number) =>
        value?.toLocaleString("pt-br", { minimumFractionDigits: 2 }) || "";
      props.setFormResumoPagamento((prevData: Data) => ({
        ...prevData,
        proSolutoPosChaves: toReal(
          props.dataPosChaves?.data?.valorProSolutoPosChaves
        ),
        proSolutoPosChaves2: toReal(
          props.dataPosChaves?.data?.porcentagemProSolutoPosChaves
        ),
      }));
      setVerifyInfo(false);
    }
  }, [props.dataPosChaves]);

  const onBlurAplicarDesconto = () => {
    props.mutateAplicarDesconto(undefined, {
      onSuccess: (data: any) => {
        const updatedValorPropostaUnidade =
          data?.data?.valorPropostaParaUnidade;
        const valorReaisDesconto = data?.data?.desconto?.valorReais;

        props.setFormResumoPagamento((prevData: Data) => ({
          ...prevData,
          valorDesconto:
            prevData.descontoSolicitadoRadio === "1"
              ? data?.data?.desconto?.valorPorcentagem
              : data?.data?.desconto?.valorReais,
          valorPropostaUnidade: updatedValorPropostaUnidade,
        }));

        // Passamos o updatedValorPropostaUnidade aqui
        props.mutateResumoPagamento(
          valorReaisDesconto,
          false,
          updatedValorPropostaUnidade
        );
        props.setSaveValueReal(valorReaisDesconto);
      },
    });

    setVerifyInfo(true);
  };

  const onBlurPosChaves = () => {
    props.mutatePosChaves({
      idLeadCorretor: params.idLeadCorretor,
      idUnidade: params.idUnidade,
      porcentagem: props.formResumoPagamento.porcentagem,
      valorProposta: props.formResumoPagamento.valorPropostaUnidade,
      valorTotal: props.dataDadosUnidade.valorTabela,
    });
  };

  return (
    <S.Container>
      <S.Row>
        <S.Title>Resumo de Pagamento</S.Title>
        {props.isLoadingResumoPagamento && (
          <MaterialUI.CircularProgress style={{ color: "blue" }} size={22} />
        )}
      </S.Row>
      <S.BodyContainer>
        <S.Row>
          <S.Column>
            <S.TitleInfo>Ato *</S.TitleInfo>
            <S.TextField
              InputLabelProps={{ shrink: false }}
              variant="outlined"
              fullWidth
              error={props.errorAto}
              onFocus={() => props.setIsFieldFocused(true)}
              onBlur={() => {
                props.handleValidateDadosAvaliacaoAto();
                props.setIsFieldFocused(false);
              }}
              onChange={(e: any) => {
                props.setFormResumoPagamento({
                  ...props.formResumoPagamento,
                  ato: formatCurrency(e.target.value),
                });
                if (props.errorAto) {
                  props.setErrorAto(false);
                }
              }}
              value={props.formResumoPagamento.ato}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">R$</InputAdornment>
                ),
                inputProps: {
                  style: { textAlign: "right" },
                },
              }}
            />
          </S.Column>
          <S.Column>
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <S.TitleInfo>Anual Solicitada</S.TitleInfo>
              {isAnualPersonalizadaDisabled ? (
                <S.WarningMessage color="red" fontSize="16px">
                  <LuInfo />ﾠ Anual Personalizada
                </S.WarningMessage>
              ) : (
                props.dataResumoPagamento?.data?.valorMaximoAnualPermitido && (
                  <S.WarningMessage>
                    <LuInfo />ﾠ
                    {props.dataResumoPagamento?.data?.valorMaximoAnualPermitido}
                  </S.WarningMessage>
                )
              )}
            </div>
            <S.TextField
              InputLabelProps={{ shrink: false }}
              variant="outlined"
              fullWidth
              disabled={
                isAnualPersonalizadaDisabled ||
                props.dataResumoPagamento?.data?.editavel === false
              }
              onFocus={() => props.setIsFieldFocused(true)}
              onBlur={() => {
                props.handleValidateDadosAvaliacaoAto();
                props.setIsFieldFocused(false);
              }}
              onChange={(e: any) =>
                props.setFormResumoPagamento({
                  ...props.formResumoPagamento,
                  anualSolicitada: formatCurrency(e.target.value),
                })
              }
              value={
                isAnualPersonalizadaDisabled
                  ? ""
                  : props.formResumoPagamento.anualSolicitada
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">R$</InputAdornment>
                ),
                inputProps: {
                  style: { textAlign: "right" },
                },
              }}
            />
          </S.Column>
        </S.Row>
        <S.Row>
          <S.Column>
            <S.BodyFieldContainer>
              <S.FieldHeader>
                <S.TitleInfo>Pro Soluto pós chaves</S.TitleInfo>
                <S.RadioGroup>
                  <S.StyledRadio
                    color="primary"
                    size="small"
                    value="1"
                    disabled={
                      props.formResumoPagamento?.numeroParcelas === "" ||
                      props.dataResumoPagamento?.data
                        ?.porcentagemProSolutoPosChaves === null ||
                      props.dataResumoPagamento?.data?.editavel === false
                    }
                    checked={
                      props.formResumoPagamento.proSolutoPosChavesRadio === "1"
                    }
                    onClick={() => {
                      // onBlurPosChaves();
                    }}
                    onChange={(e: any) =>
                      props.setFormResumoPagamento((prevData: Data) => ({
                        ...prevData,
                        proSolutoPosChavesRadio: "1",
                      }))
                    }
                  />
                  <S.TitleInfo style={{ margin: "0" }}>Sim</S.TitleInfo>
                  <S.StyledRadio
                    color="primary"
                    size="small"
                    value="2"
                    checked={
                      props.formResumoPagamento.proSolutoPosChavesRadio === "2"
                    }
                    disabled={
                      props.dataResumoPagamento?.data?.editavel === false
                    }
                    onChange={() => {
                      props.setFormResumoPagamento((prevData: Data) => ({
                        ...prevData,
                        proSolutoPosChavesRadio: "2",
                        // Atualiza as informações com os valores vindos do data.proSolutoPosChaves
                        proSolutoPosChaves: toReal(
                          props.dataResumoPagamento?.data?.proSolutoPosChaves
                            ?.valor
                        ),
                        proSolutoPosChaves2: toReal(
                          props.dataResumoPagamento?.data?.proSolutoPosChaves
                            ?.porcentagem
                        ),
                      }));
                    }}
                  />
                  <S.TitleInfo style={{ margin: "0" }}>Não</S.TitleInfo>
                </S.RadioGroup>
              </S.FieldHeader>
              <S.Row>
                <S.Column>
                  <S.TitleInfo>Porcentagem</S.TitleInfo>
                  <S.TextField
                    InputLabelProps={{ shrink: false }}
                    variant="outlined"
                    disabled={
                      props.formResumoPagamento.proSolutoPosChavesRadio !==
                        "1" ||
                      props.dataResumoPagamento?.data?.editavel === false
                    }
                    fullWidth
                    // onBlur={() => onBlurPosChaves()}
                    onChange={(e: any) =>
                      props.setFormResumoPagamento({
                        ...props.formResumoPagamento,
                        porcentagem: formatCurrencyLimitedAt5(e.target.value),
                      })
                    }
                    value={props.formResumoPagamento?.porcentagem}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">%</InputAdornment>
                      ),
                      inputProps: {
                        style: { textAlign: "right" },
                      },
                    }}
                  />
                </S.Column>
                <S.Column>
                  <S.TitleInfo>Número de Parcelas</S.TitleInfo>
                  <S.TextField
                    InputLabelProps={{ shrink: false }}
                    variant="outlined"
                    disabled
                    fullWidth
                    // onChange={(e: any) =>
                    //   props.setFormResumoPagamento({
                    //     ...props.formResumoPagamento,
                    //     numeroParcelas: e.target.value,
                    //   })
                    // }
                    value={props.formResumoPagamento?.numeroParcelas}
                    InputProps={{
                      // startAdornment: (
                      //   <InputAdornment position="start">
                      //     {props.formResumoPagamento.proSolutoPosChavesRadio ===
                      //     "1" ? null : (
                      //       <IoMdLock size={20} />
                      //     )}
                      //   </InputAdornment>
                      // ),
                      inputProps: {
                        style: { textAlign: "right" },
                      },
                    }}
                  />
                </S.Column>
              </S.Row>
            </S.BodyFieldContainer>
          </S.Column>
          <S.Column>
            <S.BodyFieldContainer>
              <S.FieldHeader style={{ padding: "0" }}>
                <S.TitleInfo>Desconto Solicitado</S.TitleInfo>
                <S.RadioGroup>
                  <S.StyledRadio
                    color="primary"
                    size="small"
                    value="1"
                    disabled={
                      props.dataResumoPagamento?.data?.editavel === false
                    }
                    checked={
                      props.formResumoPagamento.descontoSolicitadoRadio === "1"
                    }
                    onChange={(e: any) => {
                      props.setFormResumoPagamento({
                        ...props.formResumoPagamento,
                        descontoSolicitadoRadio: "1",
                      });
                      setVerifyReset(true);
                    }}
                  />
                  <S.TitleInfo style={{ margin: "0" }}>R$</S.TitleInfo>
                  <S.StyledRadio
                    color="primary"
                    size="small"
                    value="2"
                    disabled={
                      props.dataResumoPagamento?.data?.editavel === false
                    }
                    checked={
                      props.formResumoPagamento.descontoSolicitadoRadio === "2"
                    }
                    onChange={(e: any) => {
                      props.setFormResumoPagamento({
                        ...props.formResumoPagamento,
                        descontoSolicitadoRadio: "2",
                      });
                      setVerifyReset(true);
                    }}
                  />
                  <S.TitleInfo style={{ margin: "0" }}>%</S.TitleInfo>
                </S.RadioGroup>
                <S.TextField
                  InputLabelProps={{ shrink: false }}
                  variant="outlined"
                  onFocus={() => props.setIsFieldFocused(true)}
                  onBlur={() => {
                    onBlurAplicarDesconto();
                    props.setIsFieldFocused(false);
                  }}
                  disabled={
                    props.formResumoPagamento.descontoSolicitadoRadio === "0" ||
                    props.dataResumoPagamento?.data?.editavel === false
                  }
                  onChange={(e: any) =>
                    props.setFormResumoPagamento({
                      ...props.formResumoPagamento,
                      descontoSolicitadoInput: formatCurrency(e.target.value),
                    })
                  }
                  value={props.formResumoPagamento.descontoSolicitadoInput}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        {props.formResumoPagamento.descontoSolicitadoRadio ===
                        "1"
                          ? "R$"
                          : props.formResumoPagamento
                              .descontoSolicitadoRadio === "2"
                          ? "%"
                          : ""}
                      </InputAdornment>
                    ),
                    inputProps: {
                      style: { textAlign: "right" },
                    },
                  }}
                />
              </S.FieldHeader>
              <S.Row style={{ paddingTop: "14px" }}>
                <S.Column>
                  <S.TitleInfo>Valor do Desconto</S.TitleInfo>
                  <S.TextField
                    InputLabelProps={{ shrink: false }}
                    variant="outlined"
                    disabled
                    fullWidth
                    onChange={(e: any) =>
                      props.setFormResumoPagamento({
                        ...props.formResumoPagamento,
                        valorDesconto: formatCurrency(e.target.value),
                      })
                    }
                    value={props.formResumoPagamento.valorDesconto}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          {props.formResumoPagamento.descontoSolicitadoRadio ===
                          "1" ? (
                            "%"
                          ) : props.formResumoPagamento
                              .descontoSolicitadoRadio === "2" ? (
                            "R$"
                          ) : (
                            <IoMdLock size={20} />
                          )}
                        </InputAdornment>
                      ),
                      inputProps: {
                        style: { textAlign: "right" },
                      },
                    }}
                  />
                </S.Column>
              </S.Row>
            </S.BodyFieldContainer>
          </S.Column>
        </S.Row>
        <S.Row>
          <S.Column>
            <S.TitleInfo>Valor da proposta para Unidade</S.TitleInfo>
            <S.TextField
              InputLabelProps={{ shrink: false }}
              variant="outlined"
              disabled
              fullWidth
              value={props.formResumoPagamento.valorPropostaUnidade}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">R$</InputAdornment>
                ),
                inputProps: {
                  style: { textAlign: "right" },
                },
              }}
            />
          </S.Column>
          <S.Column>
            <S.TitleInfo>Avaliação considerada</S.TitleInfo>
            <S.TextField
              InputLabelProps={{ shrink: false }}
              variant="outlined"
              disabled
              fullWidth
              value={props.formResumoPagamento?.avaliacaoConsiderada}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">R$</InputAdornment>
                ),
                inputProps: {
                  style: { textAlign: "right" },
                },
              }}
            />
          </S.Column>
          <S.Column>
            <S.TitleInfo>FGTS + Subsidio + Beneficio Extra</S.TitleInfo>
            <S.TextField
              InputLabelProps={{ shrink: false }}
              variant="outlined"
              disabled
              fullWidth
              value={props.formResumoPagamento?.fgtsSubsidio}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">R$</InputAdornment>
                ),
                inputProps: {
                  style: { textAlign: "right" },
                },
              }}
            />
          </S.Column>
        </S.Row>
        <S.Row>
          <S.Column>
            <S.TitleInfo>Financiamento utilizado</S.TitleInfo>
            <S.TextField
              InputLabelProps={{ shrink: false }}
              variant="outlined"
              fullWidth
              value={props.formResumoPagamento?.financiamentoUtilizado}
              onFocus={() => props.setIsFieldFocused(true)}
              onBlur={() => {
                props.handleValidateDadosAvaliacaoAto();
                props.setIsFieldFocused(false);
              }}
              disabled={
                props.isLoadingResumoPagamento ||
                props.dataResumoPagamento?.data?.editavel === false
              }
              onChange={(e: any) => {
                props.setFormResumoPagamento({
                  ...props.formResumoPagamento,
                  financiamentoUtilizado: formatCurrencyLimited(e.target.value),
                });
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">R$</InputAdornment>
                ),
                inputProps: {
                  style: { textAlign: "right" },
                },
              }}
            />
          </S.Column>
          <S.Column>
            <S.TitleInfo>FGTS Utilizado</S.TitleInfo>
            <S.TextField
              InputLabelProps={{ shrink: false }}
              variant="outlined"
              fullWidth
              value={props.formResumoPagamento?.fgtsUtilizado}
              onFocus={() => props.setIsFieldFocused(true)}
              onBlur={() => {
                props.handleValidateDadosAvaliacaoAto();
                props.setIsFieldFocused(false);
              }}
              disabled={
                props.isLoadingResumoPagamento ||
                props.dataResumoPagamento?.data?.editavel === false
              }
              onChange={(e: any) => {
                props.setFormResumoPagamento({
                  ...props.formResumoPagamento,
                  fgtsUtilizado: formatCurrency(e.target.value),
                });
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">R$</InputAdornment>
                ),
                inputProps: {
                  style: { textAlign: "right" },
                },
              }}
            />
          </S.Column>
          <S.Column>
            <S.TitleInfo>PMT Utilizada</S.TitleInfo>
            <S.TextField
              InputLabelProps={{ shrink: false }}
              variant="outlined"
              disabled
              fullWidth
              value={props.formResumoPagamento?.pmtUtilizada}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">R$</InputAdornment>
                ),
                inputProps: {
                  style: { textAlign: "right" },
                },
              }}
            />
          </S.Column>
        </S.Row>
        <S.Row>
          <S.Column>
            <S.TitleInfo>Pro Soluto pré chaves</S.TitleInfo>
            <S.TextField
              InputLabelProps={{ shrink: false }}
              variant="outlined"
              disabled
              fullWidth
              value={props.formResumoPagamento.proSolutoPreChaves}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">R$</InputAdornment>
                ),
                inputProps: {
                  style: { textAlign: "right" },
                },
              }}
            />
          </S.Column>
          <S.Column>
            <S.TitleInfo>ﾠ</S.TitleInfo>
            <S.TextField
              InputLabelProps={{ shrink: false }}
              variant="outlined"
              disabled
              fullWidth
              onChange={(e: any) =>
                props.setFormResumoPagamento({
                  ...props.formResumoPagamento,
                  proSolutoPreChaves2: formatCurrency(e.target.value),
                })
              }
              value={props.formResumoPagamento.proSolutoPreChaves2}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">%</InputAdornment>
                ),
                inputProps: {
                  style: { textAlign: "right" },
                },
              }}
            />
          </S.Column>
          {/* <S.Column>
            <S.TitleInfo>Pro Soluto pós chaves</S.TitleInfo>
            <S.TextField
              InputLabelProps={{ shrink: false }}
              variant="outlined"
              disabled
              fullWidth
              value={props.formResumoPagamento.proSolutoPosChaves}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">R$</InputAdornment>
                ),
                inputProps: {
                  style: { textAlign: "right" },
                },
              }}
            />
          </S.Column>
          <S.Column>
            <S.TitleInfo>ﾠ</S.TitleInfo>
            <S.TextField
              InputLabelProps={{ shrink: false }}
              variant="outlined"
              disabled
              fullWidth
              onChange={(e: any) =>
                props.setFormResumoPagamento({
                  ...props.formResumoPagamento,
                  proSolutoPosChaves2: formatCurrency(e.target.value),
                })
              }
              value={props.formResumoPagamento.proSolutoPosChaves2}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">%</InputAdornment>
                ),
                inputProps: {
                  style: { textAlign: "right" },
                },
              }}
            />
          </S.Column> */}
        </S.Row>
        <S.Row>
          <S.Column>
            <S.TitleInfo>Comprometimento Mensais Médio</S.TitleInfo>
            <S.TextField
              InputLabelProps={{ shrink: false }}
              variant="outlined"
              disabled
              fullWidth
              value={props.formResumoPagamento.comprometimentoMensaisMedio}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">%</InputAdornment>
                ),
                inputProps: {
                  style: { textAlign: "right" },
                },
              }}
            />
          </S.Column>
          <S.Column>
            <S.TitleInfo>Comprometimento Mensais Maximo</S.TitleInfo>
            <S.TextField
              InputLabelProps={{ shrink: false }}
              variant="outlined"
              disabled
              fullWidth
              onChange={(e: any) =>
                props.setFormResumoPagamento({
                  ...props.formResumoPagamento,
                  comprometimentoMensaisMaximo: formatCurrency(e.target.value),
                })
              }
              value={props.formResumoPagamento.comprometimentoMensaisMaximo}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">%</InputAdornment>
                ),
                inputProps: {
                  style: { textAlign: "right" },
                },
              }}
            />
          </S.Column>
        </S.Row>
      </S.BodyContainer>
    </S.Container>
  );
}
